import { DealerInfo } from '@appTypes/DealerInfo';
import { FeatureToggle } from '@appTypes/FeatureToggle';
import { LocalStorageResource, NotificationResource } from '@appTypes/NotificationResource';
import { NotificationHeaderType } from '@enums/NotificationType';
import dayjs from 'dayjs';
import moment from 'moment';
import { BrandsService } from '../shared/BrandsService';
import { formatDateWithUtc, getExpireDate, isSameOrBefore, timeStrToNum } from './DateUtils';
import { capitalizeAll, getGuid, hashCode } from './HelperFunctions';

const filterNotificationBasedOnFeatureToggleAndPackageType = (
  notifications: NotificationResource[],
  featureToggle: FeatureToggle,
  isIncentiveAdvantageType: boolean,
  isEssentialWithTransUnion: boolean,
) => {
  if (!featureToggle.privateIncentive) {
    notifications = notifications.filter((x) => x.notificationType !== 'PrivateIncentive');
  }
  if (isIncentiveAdvantageType) {
    notifications = notifications.filter(
      (x) =>
        (x.notificationType !== 'AutoActivatedBySMm' &&
          x.notificationType !== 'ServiceAppointment') ||
        (x.notificationType === 'ServiceAppointment' && x.bps != null),
    );
  } else if (isEssentialWithTransUnion) {
    notifications = notifications.filter(
      (x) =>
        x.notificationType !== 'ServiceAppointment' ||
        (x.notificationType === 'ServiceAppointment' && x.bps != null),
    );
  }

  return notifications;
};

const filterNotificationsBasedOnActiveService = (
  notifications: NotificationResource[],
  dealerInfos: DealerInfo[],
) => {
  notifications = notifications.filter(
    (x) =>
      x.notificationType !== 'AutoActivatedBySMm' ||
      x.dealerId?.slice(0, 2).toLowerCase() === 'bm' ||
      x.dealerId?.slice(0, 2).toLowerCase() === 'mi' ||
      (x.notificationType === 'AutoActivatedBySMm' &&
        dealerInfos.find((d) => d.dealerId === x.dealerId)?.services.hasTransUnion),
  );
  return notifications;
};

export const localStorageData = (): LocalStorageResource => {
  return JSON.parse(localStorage.getItem('ls.ammWeb.notifications.list1') || '{}');
};

const getVehicleInfo = (item: NotificationResource) => {
  if (item.trim) {
    const modelName = BrandsService.getByBrandId(item.brandId || 1);

    if (item.isTrimConfirmationRequired == true && item.bps == null) {
      item.vehicleInformation = modelName + ' Trim Unconfirmed';
    } else {
      const modelYear = item.vehicleInformation.slice(0, 4);
      item.vehicleInformation = modelYear + ' ' + modelName + ' ' + item.trim;
    }
    return item.vehicleInformation;
  } else {
    return item.vehicleInformation;
  }
};

export const getNotificationIcon = (item: NotificationResource): string => {
  switch (item.notificationType) {
    case 'Anniversary':
      return 'score-product';

    case 'Birthday':
      return 'birthday';

    case 'FinalPayment':
      return 'score-payment';

    case 'ServiceAppointment':
      return 'score-warranty';

    case 'AutoActivatedBySMm':
      return 'conquest';

    case 'CampaignQrCodeScan':
    case 'ShowroomUrlQrCodeScan':
      return 'qr-code';

    case 'CampaignUrlClick':
      return 'ext-link';

    case 'EmailCampaignClick':
    case 'ConquestCampaignClick':
    case 'ShowroomUrlClicked':
      return 'campaign';

    case 'AssignedDeal':
      return item.notificationValue === 'MarketConquest'
        ? 'am-icon-market-conquest1'
        : 'am-icon-reassign-deal';

    case 'AssignedMarketConquestDeal':
      return 'market-conquest1';

    case 'PrivateIncentive':
      return 'private-incentive';

    case 'MdlServiceArrival':
      return 'service';

    case 'ShowroomOfferSubmitted':
      return 'showroom';
    case 'ShopperActivityWebsiteVisit':
    case 'ShopperActivityVehicleViewed':
    case 'ShopperActivityLeadSubmitted':
      return 'visit';
    default:
      return '';
  }
};

const isPrivateIncentiveExpiring = (item: NotificationResource) => {
  return isSameOrBefore(item.date, dayjs().add(14, 'day').toDate());
};

export const isTransUnionNotification = (item: NotificationResource) => {
  return item.notificationType === 'AutoActivatedBySMm' && item.notificationValue === 'TransUnion';
};

const getLeadSource = (item: NotificationResource, isTransUnionEnabled: boolean) => {
  if (isTransUnionNotification(item)) {
    return 'TransUnion';
  }

  const brandCode = item.dealerId?.substring(0, 2).toLowerCase() || 'AC';
  if (isTransUnionEnabled || ['bm', 'mi'].includes(brandCode)) {
    return brandCode === 'bm' ? 'BMWFS' : 'MiniFS';
  }

  return '';
};

const getNotificationText = (item: NotificationResource, isTransUnionEnabled: boolean) => {
  const privateIncentive =
    item.notificationValue == '1' ? 'Private Incentive ' : 'Private Incentives ';
  const privateIncentiveis =
    item.notificationValue == '1' ? 'Private Incentive is ' : 'Private Incentives are ';
  if (item.notificationType === 'Anniversary') {
    return `<span>
        Today is <b>${item.name}'s</b> ${item.notificationValue} year anniversary.
      </span>`;
  } else if (item.notificationType === 'Birthday') {
    return `<span>
        Today is <b>${item.name}'s</b> birthday.
      </span>`;
  } else if (item.notificationType === 'FinalPayment') {
    return `<span>
        <b>${item.name}'s</b> final payment is due tomorrow.
      </span>`;
  } else if (item.notificationType === 'ServiceAppointment') {
    return `<span>
        <b>${item.name}'s</b> service appointment is <br /> <strong>${item.appointmentText}</strong> at <strong>${item.appointmentTime}</strong>.
      </span>`;
  } else if (item.notificationType === 'AutoActivatedBySMm') {
    return `<span>
        You have <strong>a new Service-to-Sales deal</strong> <br /> (lead source: ${getLeadSource(item, isTransUnionEnabled)})
      </span>`;
  } else if (item.notificationType === 'CampaignQrCodeScan') {
    return `<span>
        <b>${item.name}</b> just scanned QR code.
      </span>`;
  } else if (item.notificationType === 'CampaignUrlClick') {
    return `<span>
        <b>${item.name}</b> just visited dealer's website.
      </span>`;
  } else if (item.notificationType === 'EmailCampaignClick' && !item.notificationValue) {
    return `<span>
        <b>${item.name}</b> just viewed the predictive campaign offer.
      </span>`;
  } else if (
    item.notificationType == 'EmailCampaignClick' &&
    item.notificationValue == 'InventoryShortage'
  ) {
    return `<span>
        <b>${item.name}</b> just engaged with the inventory campaign offer.
      </span>`;
  } else if (item.isCampaignViewMarketConquestDeal) {
    return `<span>
        <b>${capitalizeAll(item.name)}</b> just viewed the <b>Market Conquest Offer</b>.
      </span>`;
  } else if (item.notificationType === 'AssignedDeal') {
    return item.notificationValue !== 'MarketConquest'
      ? `<span>
        You have been assigned a new deal: <b>${capitalizeAll(item.name)}</b>.
      </span>`
      : `<span>
        You have been assigned a new <b>Market Conquest</b> customer: <b>${capitalizeAll(item.name)}</b>.
      </span>`;
  } else if (item.notificationType === 'AssignedMarketConquestDeal') {
    return `<span>
        You have been assigned a new <b>Market Conquest</b> customer: <b>${capitalizeAll(item.name)}</b>.
      </span>`;
  } else if (item.notificationType === 'PrivateIncentive') {
    return `<span>
    <b>${item.notificationValue}</b>
    ${isPrivateIncentiveExpiring(item) ? `${privateIncentive} will be expiring on` : `new ${privateIncentiveis} now available, expiring on  `}
        <b>${formatDateWithUtc(item.date, 'MM/DD/YY')}</b>.
      </span>`;
  } else if (item.notificationType === 'MdlServiceArrival') {
    return `<span>
        <b>${item.name}</b> has arrived for service.
      </span>`;
  } else if (item.notificationType === 'ShowroomUrlClicked') {
    return `<span>
        <b>${item.name}</b> has just clicked link and viewed Showroom page.
      </span>`;
  } else if (item.notificationType === 'ShowroomUrlQrCodeScan') {
    return `<span>
        <b>${item.name}</b> has just scanned QR and viewed Showroom page.
      </span>`;
  } else if (item.notificationType === 'ShowroomOfferSubmitted') {
    return `<span>
        <b>${item.name}</b> just submitted a <b>showroom offer</b>.
      </span>`;
  } else if (item.notificationType === 'ShopperActivityWebsiteVisit') {
    return `<span>
      <b>${item.name}</b> visited the website.
    </span>`;
  } else if (item.notificationType === 'ShopperActivityVehicleViewed' && item.notificationValue) {
    return `<span>
    <b>${item.name}</b> viewed a vehicle on the
    ${
      item?.notificationValue
        ? `<a href="${item?.notificationValue}" target="_blank" rel="noopener noreferrer">
        website.
      </a>`
        : `website`
    }
    </span>`;
  } else if (item.notificationType === 'ShopperActivityLeadSubmitted') {
    return `<span>
      <b>${item.name}</b> submitted a lead on the
      ${
        item?.notificationValue
          ? `<a href="${item?.notificationValue}" target="_blank" rel="noopener noreferrer">
        website.
      </a>`
          : `website`
      }
    </span>`;
  }
  return `<span></span>`;
};

export const updateNotification = (
  notification: NotificationResource,
  isTransUnionEnabled: boolean,
) => {
  const today = dayjs();
  const tomorrow = dayjs().add(1, 'day');
  const dayAfterTomorrow = dayjs().add(2, 'day');

  notification.id = notification.id ? notification.id : getGuid();
  notification.isNew = false;
  notification.isRead = false;
  notification.toolTip = 'Mark as Read';
  notification.isRfid =
    notification.notificationType === 'MdlServiceArrival' &&
    notification.notificationValue === 'RFID';
  notification.isMdl =
    notification.notificationType === 'MdlServiceArrival' &&
    notification.notificationValue === 'MDL';
  notification.isTextWithIcon =
    notification.isConquest ||
    notification.isPrivateIncentiveApplied ||
    !(
      notification.notificationType === 'MdlServiceArrival' &&
      notification.notificationValue === 'CDK'
    );
  notification.isAssignedMarketConquestDeal =
    notification.notificationType == 'AssignedMarketConquestDeal' ||
    (notification.notificationType == 'AssignedDeal' &&
      notification.notificationValue == 'MarketConquest');
  notification.icon = getNotificationIcon(notification);
  notification.isCampaignViewMarketConquestDeal =
    notification.notificationType == 'ConquestCampaignClick' ||
    (notification.notificationType == 'EmailCampaignClick' &&
      notification.notificationValue == 'MarketConquest');
  notification.vehicleInfo = getVehicleInfo(notification);

  switch (<any>notification.dealType) {
    case 'Lease':
      notification.dealTypeSortValue = 1;
      break;
    case 'Finance':
      notification.dealTypeSortValue = 2;
      break;
    case 'Cash':
      notification.dealTypeSortValue = 4;
      break;
    default:
      break;
  }

  if (notification.notificationType === 'ServiceAppointment') {
    const aptDate = dayjs(notification.appointmentDate);
    notification.appointmentTimeCalculated = timeStrToNum(notification.appointmentTime);
    notification.appointmentTime = notification.appointmentTime.replace(/^0/, '');

    if (today.isSame(aptDate, 'day')) {
      notification.appointmentText = 'today';
    } else if (tomorrow.isSame(aptDate, 'day')) {
      notification.appointmentText = 'tomorrow';
    } else if (dayAfterTomorrow.isSameOrBefore(aptDate, 'day')) {
      // display the day of the week
      notification.appointmentText = dayjs(aptDate).format('dddd');
    }
  }
  notification.notificationText = getNotificationText(notification, isTransUnionEnabled);

  if (notification.notificationType === 'PrivateIncentive') {
    const notificationExpDate = new Date(notification.date);
    notification.date = new Date(
      notificationExpDate.getUTCFullYear(),
      notificationExpDate.getUTCMonth(),
      notificationExpDate.getUTCDate(),
      0,
      0,
      0,
    ).toString();
  }
};

export const updateHash = (notification: NotificationResource) => {
  if (notification.notificationType === 'PrivateIncentive') {
    notification.hash = hashCode(
      (notification.notificationValue || '') +
        notification.dealerId +
        notification.notificationType +
        notification.date,
    );
  } else if (notification.notificationType === 'ServiceAppointment') {
    notification.hash = hashCode(
      (notification.notificationValue || '') +
        notification.dealerId +
        notification.dmsAppointmentNumber,
    );
  } else {
    notification.hash = hashCode(
      notification.vin + notification.notificationType + notification.date,
    );
    if (!notification.vin) {
      notification.hash = hashCode(
        notification.leadId + notification.notificationType + notification.date,
      );
    }
  }
};

export const sortNotifications = (
  notifications: Array<NotificationResource>,
  activeFilter: string = '',
): Array<NotificationResource> => {
  const defaultSortingConfiguration = [
    { field: 'dealTypeSortValue', sortingStrategy: sortByNumberAsc },
    { field: 'bps', sortingStrategy: sortByNumbeDesc },
  ];

  const sortingConfigurationMap: Map<
    string,
    Array<{ field: string; sortingStrategy: NotificationsSortingStrategy }>
  > = new Map([
    [
      'ServiceAppointment',
      [
        { field: 'appointmentDate', sortingStrategy: sortByDateAsc },
        { field: 'bps', sortingStrategy: sortByNumbeDesc },
      ],
    ],
    ['MdlServiceArrival', [{ field: 'date', sortingStrategy: sortByDateDesc }]],
    ['EmailCampaignClick', [{ field: 'date', sortingStrategy: sortByDateDesc }]],
    ['ConquestCampaignClick', [{ field: 'date', sortingStrategy: sortByDateDesc }]],
    ['AssignedDeal', [{ field: 'date', sortingStrategy: sortByDateDesc }]],
    ['Showroom', [{ field: 'date', sortingStrategy: sortByDateDesc }]],
    ['WebsiteActivity', [{ field: 'date', sortingStrategy: sortByDateAndNotificationType }]],
  ]);

  const sortingConfiguration = sortingConfigurationMap.get(activeFilter);
  const sortersConfig: Array<{
    field: string;
    sortingStrategy: NotificationsSortingStrategy;
  }> = sortingConfiguration ? sortingConfiguration : defaultSortingConfiguration;

  return [...notifications].sort((leftNotif, rightNotif) => {
    const result = sortersConfig
      .map(
        (sorter) => (left: NotificationResource, right: NotificationResource) =>
          sorter.sortingStrategy(sorter.field, left, right),
      )
      .map((sorter) => sorter(leftNotif, rightNotif))
      .find((result) => result !== 0);

    return result || 0;
  });
};

type NotificationsSortingStrategy = (
  field: string,
  left: NotificationResource,
  right: NotificationResource,
) => number;

const sortByDateAsc: NotificationsSortingStrategy = (
  field: string,
  left: NotificationResource,
  right: NotificationResource,
) => {
  return moment.utc(moment(left[field])).diff(moment(right[field]));
};

const sortByDateDesc: NotificationsSortingStrategy = (
  field: string,
  left: NotificationResource,
  right: NotificationResource,
) => {
  return moment.utc(moment(right[field])).diff(moment(left[field]));
};

const sortByNumberAsc: NotificationsSortingStrategy = (
  field: string,
  left: NotificationResource,
  right: NotificationResource,
) => {
  return left[field] - right[field];
};

const sortByNumbeDesc: NotificationsSortingStrategy = (
  field: string,
  left: NotificationResource,
  right: NotificationResource,
) => {
  return right[field] - left[field];
};
const notificationTypePriority: Record<string, number> = {
  ShopperActivityLeadSubmitted: 1,
  ShopperActivityVehicleViewed: 2,
  ShopperActivityWebsiteVisit: 3,
};

const sortByDateAndNotificationType: NotificationsSortingStrategy = (
  field: string,
  left: NotificationResource,
  right: NotificationResource,
) => {
  // Compare by date (newest first)
  const dateComparison = new Date(right[field]).getTime() - new Date(left[field]).getTime();

  if (dateComparison !== 0) {
    return dateComparison;
  }

  // Compare by VIN
  const vinComparison = left.vin.localeCompare(right.vin);
  if (vinComparison !== 0) {
    return vinComparison;
  }

  // If VIN & Date are the same, compare by notificationType priority
  const priorityA = notificationTypePriority[left.notificationType] || 99;
  const priorityB = notificationTypePriority[right.notificationType] || 99;
  return priorityA - priorityB;
};
export const setLocalStorage = (
  notifs: Array<NotificationResource>,
  showReadNts: boolean,
  isTransUnionEnabled: boolean,
  activeFilter: NotificationHeaderType,
  isPrimaryUser: boolean,
  storeCode: string,
) => {
  const storage = localStorageData();
  const data: LocalStorageResource = {
    notifications: notifs,
    activeSearchFilter: activeFilter,
    expireDate: getExpireDate(),
    showReadNotifications: showReadNts,
    isTransUnionEnabled: isTransUnionEnabled,
  };

  if (storage && !isPrimaryUser) {
    storage[`${storeCode}`] = data;
    localStorage.setItem('ls.ammWeb.notifications.list1', JSON.stringify(storage));
  } else {
    localStorage.setItem('ls.ammWeb.notifications.list1', JSON.stringify(data));
  }
};

export const getMarkType = (filter: NotificationHeaderType): string => {
  switch (filter) {
    case '':
      return '';
    case NotificationHeaderType.Birthday:
      return 'Birthdays';
    case NotificationHeaderType.Anniversary:
      return 'Anniversaries';
    case NotificationHeaderType.FinalPayment:
      return 'Final Payments';
    case NotificationHeaderType.ServiceAppointment:
      return 'Service Appointments';
    case NotificationHeaderType.MdlServiceArrival:
      return 'Service Arrivals';
    case NotificationHeaderType.EmailCampaignClick:
      return 'Campaign Notifications';
    case NotificationHeaderType.AssignedDeal:
      return 'Deal Assignments';
    case NotificationHeaderType.PrivateIncentive:
      return 'Private Incentive';
    case NotificationHeaderType.Showroom:
      return 'Showroom Activity';
    case NotificationHeaderType.WebsiteActivity:
      return 'Website Activity';
    default:
      return 'All';
  }
};

export const processNotifications = (
  notifs: NotificationResource[],
  isPrimaryUser: boolean,
  featureToggle: FeatureToggle,
  storeCode: string,
  dealerInfos: DealerInfo[],
  isIncentiveAdvantageType: boolean,
  isEssentialWithTransUnion: boolean,
  isTransUnionEnabled: boolean,
) => {
  let notifications = filterNotificationBasedOnFeatureToggleAndPackageType(
    notifs,
    featureToggle,
    isIncentiveAdvantageType,
    isEssentialWithTransUnion,
  );
  notifications = filterNotificationsBasedOnActiveService(notifs, dealerInfos);
  notifications.forEach((n) => {
    updateNotification(n, isTransUnionEnabled);
  });
  // keep old notifications read state
  const hashes = {};
  let storage = localStorageData()[storeCode] || localStorageData();
  if (isPrimaryUser) {
    storage = localStorageData();
  }

  storage.notifications?.forEach((notif) => {
    hashes[notif.hash] = notif;
  });

  notifications.forEach((n, i) => {
    updateHash(n);

    const notif = hashes[n.hash];
    if (notif) {
      notifications[i].isRead = notif.isRead;
    }

    notifications[i].isConquest = n.isConquest;
    notifications[i].hasOpenRecall = featureToggle.recall && n.hasOpenRecall;
  });

  return notifications;
};
